import { ArrowDownRight, ShieldCheck, Activity, Zap } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/constants";

const Hero = () => {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative w-full overflow-hidden border-b border-white/10"
        >
            {/* Blueprint background */}
            <div className="absolute inset-0 bg-blueprint opacity-60 pointer-events-none" />
            <div className="absolute inset-0 scan-line opacity-40 pointer-events-none" />

            <div className="relative px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12 md:pb-20">
                {/* Top meta strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 md:mb-20 text-[10px] md:text-xs font-mono uppercase tracking-[0.25em] text-white/50">
                    <div data-testid="hero-meta-1" className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-[#1A9EC9] inline-block animate-pulse" />
                        Sistema · Operativo
                    </div>
                    <div data-testid="hero-meta-2" className="hidden md:block">
                        v.2026 / Edición Empresarial
                    </div>
                    <div data-testid="hero-meta-3" className="hidden md:block">
                        Cobertura · Nacional
                    </div>
                    <div data-testid="hero-meta-4" className="text-right">
                        / 001 — INICIO
                    </div>
                </div>

                {/* Main grid */}
                <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
                    <div className="col-span-12 lg:col-span-8">
                        <p
                            data-testid="hero-eyebrow"
                            className="text-xs md:text-sm font-mono tracking-[0.3em] uppercase text-white/50 mb-6"
                        >
                            // Terah · Ingeniería de Infraestructura Crítica
                        </p>
                        <h1
                            data-testid="hero-title"
                            className="font-display uppercase leading-[0.85] tracking-tighter text-[11vw] md:text-[10vw] lg:text-[8.2vw]"
                        >
                            Infraestructura
                            <br />
                            <span className="inline-block">
                                que
                                <span className="text-[#1A9EC9] inline-block px-3 md:px-5">
                                    no falla
                                </span>
                            </span>
                            .
                        </h1>
                        <p
                            data-testid="hero-subtitle"
                            className="mt-8 md:mt-10 max-w-2xl font-mono text-base md:text-lg leading-relaxed text-white/70"
                        >
                            Protección, conectividad y energía continua para
                            empresas de alto rendimiento. Diseñamos, instalamos
                            y monitoreamos los sistemas que sostienen tu
                            operación, 24/7.
                        </p>

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="hero-cta-primary"
                                className="group inline-flex items-center justify-between gap-6 bg-[#1A9EC9] text-white hover:bg-white hover:text-black transition-colors px-7 py-5 font-mono text-xs tracking-[0.25em] uppercase font-bold min-w-[260px]"
                            >
                                Iniciar Proyecto
                                <ArrowDownRight
                                    size={18}
                                    className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform"
                                />
                            </a>
                            <a
                                href="#servicios"
                                data-testid="hero-cta-secondary"
                                className="group inline-flex items-center justify-between gap-6 border border-white/20 hover:border-white text-white px-7 py-5 font-mono text-xs tracking-[0.25em] uppercase font-bold min-w-[260px]"
                            >
                                Ver Servicios
                                <span className="font-display text-base">
                                    06 ÁREAS
                                </span>
                            </a>
                        </div>
                    </div>

                    {/* Right side - control panel */}
                    <div className="col-span-12 lg:col-span-4">
                        <div
                            data-testid="hero-panel"
                            className="border border-white/10 bg-black/40 backdrop-blur-sm divide-y divide-white/10"
                        >
                            <div className="p-5 flex items-center justify-between">
                                <span className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono">
                                    Estado · Live
                                </span>
                                <span className="w-2 h-2 bg-emerald-400 rounded-full inline-block animate-pulse" />
                            </div>
                            <PanelRow
                                icon={<ShieldCheck size={16} />}
                                label="Sitios protegidos"
                                value="142"
                                testid="panel-sites"
                            />
                            <PanelRow
                                icon={<Activity size={16} />}
                                label="Uptime promedio"
                                value="99.98%"
                                testid="panel-uptime"
                            />
                            <PanelRow
                                icon={<Zap size={16} />}
                                label="Respuesta SLA"
                                value="< 30 min"
                                testid="panel-sla"
                            />
                            <div className="p-5">
                                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 mb-3">
                                    Certificaciones
                                </p>
                                <div className="flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
                                    {["NFPA", "CompTIA", "CCNA", "HCIA", "HCSA"].map(
                                        (c) => (
                                            <span
                                                key={c}
                                                className="border border-white/15 px-2.5 py-1 text-white/70"
                                            >
                                                {c}
                                            </span>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const PanelRow = ({ icon, label, value, testid }) => (
    <div
        data-testid={testid}
        className="p-5 flex items-center justify-between gap-4"
    >
        <div className="flex items-center gap-3 text-white/60">
            {icon}
            <span className="text-[11px] font-mono uppercase tracking-[0.2em]">
                {label}
            </span>
        </div>
        <span className="font-display text-2xl text-white">{value}</span>
    </div>
);

export default Hero;
